import { useId, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { HugeiconsIcon } from '@hugeicons/react'
import { AlertCircleIcon, Attachment01StrokeRounded } from '@hugeicons-pro/core-stroke-rounded'
import { Box, Button, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { selectLocale } from '@/app/slices/appSlice'
import { AlertDialog } from '@/common/components'
import {
    ATTACHMENT_MAX_FILE_BYTES,
    ATTACHMENT_MAX_PER_PARENT,
    ATTACHMENT_SCAN_STATE,
} from '@/common/utils/Constants/Constants'

import AttachmentRow, { ATTACHMENT_ROW_COLUMNS, ATTACHMENT_TABLE_MIN_WIDTH } from './AttachmentRow'
import UploadRow from './UploadRow'
import UploadZone from './UploadZone'
import useAttachmentUploads, { errorMessage, UPLOAD_STATE } from './useAttachmentUploads'

// Copied from the webapp's AttachmentsPanel. The web store has no visibility toggle (staff only),
// and anonymous visitors see a prompt to sign in where the upload zone would be.

const MAX_FILE_MB = Math.round(ATTACHMENT_MAX_FILE_BYTES / (1024 * 1024))

// The API sends attachment refusals as text/plain. RTK Query cannot parse that as JSON, so it
// reports a PARSING_ERROR and keeps the HTTP status in originalStatus.
const httpStatus = (error) => error?.originalStatus ?? error?.status

/**
 * AttachmentsPanel - The attachments ledger for a web store quote. It owns the dropzone, rows,
 * delete confirmation and error messages; the caller owns fetching and the API calls.
 *
 * @param {Object} props
 * @param {Array} props.attachments - DocumentDto rows (public only, filtered by the server)
 * @param {boolean} props.canUpload - False for anonymous visitors: upload is shown but inactive
 * @param {string} [props.emptyMessage] - Shown when there are no rows; defaults to the quote wording
 * @param {boolean} [props.isLoading] - True until the first response; the count and upload wait for it
 * @param {Function} props.onDelete - (documentId) => Promise, rejects with the RTK Query error
 * @param {Function} props.onSignIn - Starts sign-in and returns the visitor to this quote
 * @param {Function} props.onUploadFile - (file, { onProgress }) => Promise for one file, see
 *   useAttachmentUploads
 * @param {Function} props.onUploadsSettled - Refreshes the rows once a batch of uploads is done
 * @param {Function} props.resolveDownloadUri - (attachment) => Promise<string|undefined>
 * @param {string} props.title - Names the level, e.g. "Part attachments", so users see where a file goes
 */
const AttachmentsPanel = ({
    attachments,
    canUpload,
    emptyMessage,
    isLoading = false,
    onDelete,
    onSignIn,
    onUploadFile,
    onUploadsSettled,
    resolveDownloadUri,
    title,
}) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const locale = useSelector(selectLocale)

    const [attachmentToDelete, setAttachmentToDelete] = useState(null)
    // Stays set after a successful delete: the row is about to vanish in the refetch, and until
    // then it must not accept a second delete.
    const [deletingId, setDeletingId] = useState(null)

    // The rows are public only, and web store uploads are always public. The server counts public
    // and private files against separate limits, so this count is the real public limit.
    const uploads = useAttachmentUploads({ usedSlots: attachments.length, onUploadFile, onUploadsSettled })
    const isFull = !isLoading && uploads.freeSlots === 0
    // Until the first response, the slot check would count zero rows and let too many files through.
    const uploadBlocked = !canUpload || isLoading || uploads.inFlight || isFull
    const rowIdPrefix = useId()
    const uploadRowId = (key) => `${rowIdPrefix}-upload-${key}`

    const problems = uploads.items.filter(({ state }) => state === UPLOAD_STATE.error).length
    const jumpToFirstProblem = () => {
        const first = uploads.items.find(({ state }) => state === UPLOAD_STATE.error)
        if (first) document.getElementById(uploadRowId(first.key))?.scrollIntoView({ block: 'nearest' })
    }

    // Header pips: stored, not accepted, on the way, free. The text next to them says the same.
    const failedCount = attachments.filter(({ scanState }) => scanState === ATTACHMENT_SCAN_STATE.Failed).length
    const sendingCount = uploads.items.filter(({ state }) =>
        [UPLOAD_STATE.waiting, UPLOAD_STATE.uploading, UPLOAD_STATE.checking, UPLOAD_STATE.done].includes(state)
    ).length
    const pips = Array.from({ length: ATTACHMENT_MAX_PER_PARENT }, (_, index) => {
        if (index < attachments.length - failedCount) return 'text.primary'
        if (index < attachments.length) return 'error.main'
        if (index < attachments.length + sendingCount) return 'primary.main'
        return 'action.disabledBackground'
    })

    // No `accept` on purpose: any file type may be uploaded, the server scans it.
    const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
        disabled: uploadBlocked,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        onDrop: (files) => files.length && uploads.addFiles(files),
    })

    const handleConfirmDelete = async () => {
        const attachment = attachmentToDelete
        setAttachmentToDelete(null)
        setDeletingId(attachment.documentId)
        try {
            await onDelete(attachment.documentId)
        } catch (error) {
            setDeletingId(null)
            const status = httpStatus(error)
            if (status === 409) {
                enqueueSnackbar(t('This file is still being checked. Try again shortly.'), { variant: 'warning' })
            } else if (status === 403) {
                enqueueSnackbar(t('You can only remove files you uploaded.'), { variant: 'error' })
            } else {
                enqueueSnackbar(errorMessage(error, t('Failed to delete attachment')), { variant: 'error' })
            }
        }
    }

    const handleDownload = async (attachment) => {
        // Open the tab now, inside the click: popup blockers only allow window.open during the
        // user's gesture, and the link may need an async refresh first. (No 'noopener' here, it
        // would return null; the opener is cut below instead.)
        const tab = window.open('', '_blank')
        if (!tab) {
            enqueueSnackbar(t('Your browser blocked the download. Allow pop-ups for this site and try again.'), {
                variant: 'warning',
            })
            return
        }

        let uri
        try {
            uri = await resolveDownloadUri(attachment)
        } catch {
            tab.close()
            enqueueSnackbar(t('Could not get the download link. Try again.'), { variant: 'error' })
            return
        }
        if (!uri) {
            tab.close()
            enqueueSnackbar(t('This file is not ready to download yet.'), { variant: 'warning' })
            return
        }

        // Keeps the new tab from reaching back into the app, as noopener would.
        tab.opener = null
        tab.location.href = uri
    }

    return (
        <Box
            {...getRootProps({
                'aria-label': title,
                component: 'section',
                // react-dropzone sets role="presentation"; this is a landmark, not decoration.
                role: 'region',
                sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                },
            })}
        >
            <input {...getInputProps({ 'aria-label': t('Upload attachments') })} />

            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'space-between',
                    mx: 1.5,
                }}
            >
                <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Typography
                        sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.75, textTransform: 'uppercase' }}
                        variant="strong1"
                    >
                        <HugeiconsIcon
                            icon={Attachment01StrokeRounded}
                            size={20}
                        />
                        {title}
                    </Typography>
                    {isLoading ? null : (
                        <Box
                            sx={{ display: 'inline-flex', gap: 0.5 }}
                            aria-hidden
                        >
                            {pips.map((color, index) => (
                                <Box
                                    // Five fixed slots; the index is the identity.
                                    key={index}
                                    sx={{ bgcolor: color, borderRadius: 1, height: 8, width: 22 }}
                                />
                            ))}
                        </Box>
                    )}
                    {isLoading ? null : (
                        <Typography
                            color="text.secondary"
                            variant="body2"
                        >
                            {t('{{used}} of {{max}} slots used · {{maxSize}} MB per file · any type', {
                                used: attachments.length,
                                max: ATTACHMENT_MAX_PER_PARENT,
                                maxSize: MAX_FILE_MB,
                            })}
                        </Typography>
                    )}
                </Box>
                {problems ? (
                    <Button
                        color="error"
                        size="small"
                        startIcon={
                            <HugeiconsIcon
                                icon={AlertCircleIcon}
                                size={16}
                            />
                        }
                        sx={{ borderRadius: 4, py: 0, textTransform: 'none' }}
                        variant="outlined"
                        onClick={jumpToFirstProblem}
                    >
                        {problems === 1 ? t('1 needs attention') : t('{{count}} need attention', { count: problems })}
                    </Button>
                ) : null}
            </Box>

            <UploadZone
                batchKeys={uploads.batchKeys}
                canUpload={canUpload}
                excess={uploads.excess}
                freeSlots={uploads.freeSlots}
                heldCount={uploads.heldCount}
                inFlight={uploads.inFlight}
                isDragActive={isDragActive}
                isLoading={isLoading}
                items={uploads.items}
                onAdd={open}
                onSendHeld={uploads.sendHeld}
                onSignIn={onSignIn}
            />

            {attachments.length || uploads.items.length ? (
                // Only the table scrolls sideways; the title and the upload zone keep the card's width.
                <Box sx={{ overflowX: 'auto' }}>
                    <Box
                        aria-label={t('Attachment list')}
                        component="ul"
                        sx={{ m: 0, minWidth: ATTACHMENT_TABLE_MIN_WIDTH, p: 0 }}
                    >
                        <Box
                            role="row"
                            sx={{
                                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                color: 'text.secondary',
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: ATTACHMENT_ROW_COLUMNS,
                                px: 1.5,
                                pb: 1,
                            }}
                        >
                            {[t('Name'), t('Uploaded'), t('Status')].map((label) => (
                                <Typography
                                    key={label}
                                    role="columnheader"
                                    sx={{ fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}
                                    variant="caption"
                                >
                                    {label}
                                </Typography>
                            ))}
                            <span />
                        </Box>
                        {attachments.map((attachment) => (
                            <AttachmentRow
                                // An anonymous visitor owns nothing, whatever the flag says.
                                attachment={canUpload ? attachment : { ...attachment, canDelete: false }}
                                busy={deletingId === attachment.documentId}
                                key={attachment.documentId}
                                locale={locale}
                                onDelete={setAttachmentToDelete}
                                onDownload={handleDownload}
                            />
                        ))}
                        {/* Files on their way in sit in the same table and keep their place. */}
                        {uploads.items.map((item) => (
                            <UploadRow
                                freeSlots={uploads.freeSlots}
                                id={uploadRowId(item.key)}
                                inFlight={uploads.inFlight}
                                item={item}
                                key={item.key}
                                onRemove={uploads.remove}
                                onRetry={uploads.retry}
                            />
                        ))}
                    </Box>
                </Box>
            ) : (
                <Typography
                    color="text.secondary"
                    sx={{ mx: 1.5 }}
                >
                    {isLoading ? t('Loading attachments…') : (emptyMessage ?? t('No attachments on this quote yet.'))}
                </Typography>
            )}

            <AlertDialog
                content={
                    <>
                        <strong>{attachmentToDelete?.documentName}</strong>
                        {' — '}
                        {t('this cannot be undone.')}
                    </>
                }
                okButtonText={t('Delete')}
                open={Boolean(attachmentToDelete)}
                title={t('Delete attachment?')}
                onCancelClose={() => setAttachmentToDelete(null)}
                onOkClose={handleConfirmDelete}
            />
        </Box>
    )
}

AttachmentsPanel.propTypes = {
    attachments: PropTypes.array.isRequired,
    canUpload: PropTypes.bool.isRequired,
    resolveDownloadUri: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSignIn: PropTypes.func.isRequired,
    onUploadFile: PropTypes.func.isRequired,
    onUploadsSettled: PropTypes.func.isRequired,
    emptyMessage: PropTypes.string,
    isLoading: PropTypes.bool,
}

export default AttachmentsPanel
